import { cn, tv } from "@pcln/horizon";
import { MULTI_DESTINATION, ROUND_TRIP } from "./constants.js";
const flightTypeVariants = {
    multiDestination: {
        gridContainer: '@3xl:grid-cols-3',
        flightTypeWrapper: '@3xl:[grid-area:unset]'
    },
    oneWay: {
        gridContainer: [
            '@3xl:grid-cols-6 @7xl:grid-cols-[1fr_1fr_1fr_1fr_max-content]',
            "@7xl:[grid-template-areas:'options_options_options_options_options'_'origin_destination_timeline_passengers_submit'_'deals_deals_deals_deals_deals']",
            "@3xl:[grid-template-areas:'options_options_options_options_options_options'_'origin_origin_origin_destination_destination_destination'_'timeline_timeline_passengers_passengers_cabin_cabin'_'submit_submit_submit_submit_submit_submit']"
        ],
        flightTypeWrapper: '@3xl:[grid-area:options]'
    },
    oneWayNoCabin: {
        gridContainer: [
            '@3xl:grid-cols-6 @7xl:grid-cols-[1fr_1fr_1fr_1fr_max-content]',
            "@7xl:[grid-template-areas:'options_options_options_options_options'_'origin_destination_timeline_passengers_submit'_'deals_deals_deals_deals_deals']",
            "@3xl:[grid-template-areas:'options_options_options_options_options_options'_'origin_origin_origin_destination_destination_destination'_'timeline_timeline_timeline_passengers_passengers_passengers'_'submit_submit_submit_submit_submit_submit']"
        ],
        flightTypeWrapper: '@3xl:[grid-area:options]'
    },
    roundTrip: {
        gridContainer: [
            '@3xl:grid-cols-6 @7xl:grid-cols-[1fr_1fr_1fr_1fr_max-content]',
            "@7xl:[grid-template-areas:'options_options_options_options_options'_'origin_destination_timeline_passengers_submit'_'deals_deals_deals_deals_deals']",
            "@3xl:[grid-template-areas:'options_options_options_options_options_options'_'origin_origin_origin_destination_destination_destination'_'timeline_timeline_passengers_passengers_cabin_cabin'_'deals_deals_deals_submit_submit_submit']"
        ],
        flightTypeWrapper: '@3xl:[grid-area:options]'
    },
    roundTripNoCabin: {
        gridContainer: [
            '@3xl:grid-cols-6 @7xl:grid-cols-[1fr_1fr_1fr_1fr_max-content]',
            "@7xl:[grid-template-areas:'options_options_options_options_options'_'origin_destination_timeline_passengers_submit'_'deals_deals_deals_deals_deals']",
            "@3xl:[grid-template-areas:'options_options_options_options_options_options'_'origin_origin_origin_destination_destination_destination'_'timeline_timeline_timeline_passengers_passengers_passengers'_'deals_deals_deals_submit_submit_submit']"
        ],
        flightTypeWrapper: '@3xl:[grid-area:options]'
    }
};
const isMultiDestinationVariants = {
    true: {
        passengersInput: '@3xl:[grid-area:unset]',
        cabinInput: '@3xl:[grid-area:unset]'
    },
    false: {
        passengersInput: '@3xl:[grid-area:passengers]',
        cabinInput: '@3xl:[grid-area:cabin]'
    }
};
const flightFormSlots = {
    gridContainer: 'grid grid-cols-1 gap-3 items-center mx-auto',
    flightTypeWrapper: [
        '@7xl:h-9 text-nowrap @7xl:[grid-area:auto/1/auto/-1] w-max grid gap-4',
        'grid-cols-[max-content_max-content] items-center [grid-area:unset]'
    ],
    inputBase: '[grid-area:unset] ',
    originInput: '[grid-area:unset] @3xl:[grid-area:origin]',
    destinationInput: '[grid-area:unset] @3xl:[grid-area:destination]',
    timelineInput: '[grid-area:unset] @3xl:[grid-area:timeline] ',
    passengersInput: '[grid-area:unset] ',
    cabinInput: '[grid-area:unset]',
    multiDestDateWrapper: '',
    packageDeals: 'flex flex-row items-center justify-start gap-2 text-sm [grid-area:unset] @3xl:[grid-area:deals] @7xl:[grid-area:auto/1/auto/-1] @7xl:order-last'
};
const horizonFlightForm = tv({
    slots: flightFormSlots,
    variants: {
        flightType: flightTypeVariants,
        isMultiDestination: isMultiDestinationVariants
    },
    defaultVariants: {
        flightType: 'oneWay',
        isMultiDestination: false
    }
});
const getFlightTypeVariant = (flightType, hideCabin)=>{
    if (flightType === MULTI_DESTINATION) return 'multiDestination';
    if (flightType === ROUND_TRIP) return hideCabin ? 'roundTripNoCabin' : 'roundTrip';
    return hideCabin ? 'oneWayNoCabin' : 'oneWay';
};
const getGridContainerClass = (flightType, addHotel, hideCabin = false)=>{
    const flightTypeVariant = getFlightTypeVariant(flightType, hideCabin);
    const isMultiDestination = flightType === MULTI_DESTINATION;
    const styles = horizonFlightForm({
        flightType: flightTypeVariant,
        isMultiDestination
    });
    return cn(styles.gridContainer(), addHotel ? 'with-hotel' : 'without-hotel', flightType.toLowerCase());
};
const getFlightTypeClass = (flightType)=>{
    const flightTypeVariant = getFlightTypeVariant(flightType, false);
    const isMultiDestination = flightType === MULTI_DESTINATION;
    const styles = horizonFlightForm({
        flightType: flightTypeVariant,
        isMultiDestination
    });
    return styles.flightTypeWrapper();
};
const getInputClass = (type, flightType = 'OW')=>{
    const isMultiDestination = flightType === MULTI_DESTINATION;
    const styles = horizonFlightForm({
        isMultiDestination
    });
    if ('packageDeals' === type) return flightFormSlots.packageDeals;
    const inputMap = {
        origin: styles.originInput,
        destination: styles.destinationInput,
        timeline: styles.timelineInput,
        passengers: styles.passengersInput,
        cabin: styles.cabinInput,
        multiDestDateWrapper: ()=>flightFormSlots.multiDestDateWrapper
    };
    const baseClass = inputMap[type]();
    if ('cabin' === type) return cn(baseClass, '@7xl:hidden');
    return baseClass;
};
const ROUTE_SWAP_BUTTON_CLASS = "absolute top-1/2 right-[-1.625rem] z-10 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-action-md shadow-sm transition bg-actionPrimary-8 text-actionPrimary-3 hover:bg-actionPrimary-9 hover:text-actionPrimary-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-actionPrimary-12 focus-visible:ring-offset-2 @3xl:flex";
export { ROUTE_SWAP_BUTTON_CLASS, getFlightTypeClass, getGridContainerClass, getInputClass, horizonFlightForm };
